import type { ResourceReferenceType } from "@mlx/contracts";
import { MessageSquareIcon } from "lucide-react";
import Link from "next/link";

import { FileGlyph } from "@/components/content/icons/file-type-icons";
import { artifactResourceVisual, ResourceIcon } from "@/components/content/resource-card";

/**
 * A resolved artifact, file or session reference: its mark (a session's icon)
 * and title, and a session's status, linked to the resource unless `link` is
 * false. `kind` is the reference's own: an artifact's kind or a file's
 * content type. RecordResourceValue resolves the reference and renders it
 * through this.
 */
export function RecordResourceLabel({ type, title, status, kind, href, link = true }: {
  type: ResourceReferenceType; title: string; status?: string; kind?: string; href: string; link?: boolean;
}) {
  const icon = type === "session" ? <MessageSquareIcon className="size-3.5 shrink-0" />
    : type === "file" ? <FileGlyph file={{ fileName: title, contentType: kind }} className="size-3.5" />
    : <ResourceIcon visual={artifactResourceVisual({ kind: kind ?? "markdown", title })} className="size-3.5" />;
  const content = <>{icon}<span className="truncate">{title}</span>
    {type === "session" && status ? <span className="text-xs text-muted-foreground">{status}</span> : null}</>;
  // Artifact IDs resolve through a server redirect to their owning session.
  // Use a document navigation for that resolver, rather than a prefetched route.
  const ResourceLink = type === "artifact" ? "a" : Link;
  return link ? <ResourceLink href={href} onClick={event => {
    event.stopPropagation();
  }} onKeyDown={event => event.stopPropagation()}
    className="inline-flex max-w-full items-center gap-1.5 text-primary hover:underline">{content}</ResourceLink> :
    <span className="inline-flex max-w-full items-center gap-1.5">{content}</span>;
}
